import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { noop } from 'ui-components/default-props/noop';

import { messages } from './messages';
import style from './ups-label-terms-modal.module.css';
import { updateSellerSettingsBulk } from '../../../pages/settings/api/save-settings';
import { saveNewSellerSettings } from '../../../store/seller/seller.actions';
import { UpsTermsCheckbox } from '../../ups-terms-checkbox/ups-terms-checkbox';
import { HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING } from '../../../wallet/utilities/constants';

/**
 * Modal for accepting UPS label terms before purchasing a UPS label
 * @param {object} props
 * @param {() => void} props.onAccept - Callback when terms are accepted
 * @param {{ closeModal: (isCancel?: boolean) => void }} props.modalFrameworkMethods - Modal framework methods
 * @returns {React.JSX.Element}
 */
export const UpsLabelTermsModal = ({ onAccept = noop, modalFrameworkMethods }) => {
  const dispatch = useDispatch();
  const { closeModal } = modalFrameworkMethods || {};
  const [upsTermsAccepted, setUpsTermsAccepted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const handleAccept = useCallback(async () => {
    if (!upsTermsAccepted) return;

    try {
      setIsSaving(true);
      setSaveError(false);
      const newSettings = await updateSellerSettingsBulk({
        [HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING]: 'true',
      });
      dispatch(saveNewSellerSettings(newSettings));
      closeModal?.(false);
      onAccept();
    } catch {
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  }, [upsTermsAccepted, dispatch, closeModal, onAccept]);

  return (
    <Modal size='medium'>
      <ModalHeader>
        <FormattedMessage {...messages.modalTitle} />
      </ModalHeader>
      <ModalBody className={style['modal-body']}>
        {saveError ? <BannerMessageV2 content={<FormattedMessage {...messages.saveError} />} status={BannerStatus.Error} /> : null}
        <UpsTermsCheckbox checked={upsTermsAccepted} onChange={setUpsTermsAccepted} />
      </ModalBody>
      <ModalFooter>
        <ModalFooterCancelButton />
        <Button disabled={!upsTermsAccepted || isSaving} onClick={handleAccept} theme='primary'>
          <FormattedMessage {...messages.acceptButton} />
        </Button>
      </ModalFooter>
    </Modal>
  );
};
